import React, { useEffect, useState } from 'react';
import { FaChevronDown, FaEye } from 'react-icons/fa';
// Migrated to TypeScript data files
import { personalInfo } from '../data/personal';
import { socialLinks } from '../data/social';
import { useColorScheme } from '../contexts/ColorSchemeContext';
import TypingAnimation from './TypingAnimation';
import ResumeModal from './ResumeModal';
import profileImage from '../assets/me3d.png';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="min-h-screen flex items-center justify-center relative overflow-hidden">

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center">
          {/* Main Content */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Profile Image */}
            <div className="mb-8 flex justify-center" style={{ background: 'transparent' }}>
              <div 
                className="p-3 rounded-lg flex items-center justify-center"
                style={{
                  background: 'transparent',
                  border: '3px solid var(--color-primary)',
                  borderRadius: '12px',
                  display: 'inline-flex',
                  minWidth: '140px',
                  minHeight: '140px'
                }}
              >
                <img 
                  src={`${profileImage}?v=${Date.now()}`} 
                  alt="Ashraf Ali" 
                  className="border-0 outline-none"
                  style={{ 
                    border: 'none', 
                    outline: 'none',
                    backgroundColor: 'transparent',
                    maxWidth: '128px',
                    maxHeight: '128px',
                    width: 'auto',
                    height: 'auto',
                    display: 'block',
                    objectFit: 'contain',
                    objectPosition: 'center center'
                  }}
                  onError={(e) => {
                    console.error('Image failed to load:', e.target.src);
                    e.target.style.display = 'none';
                  }}
                  onLoad={() => console.log('Image loaded successfully')}
                />
              </div>
            </div>

            {/* Name and Title */}
            <h1 className="text-5xl md:text-7xl font-bold mb-4">
              <span className="accent-gradient-text">
                {personalInfo.name}
              </span>
            </h1>
            
            <h2 className={`text-xl md:text-2xl font-medium mb-6 ${colors.textSecondary} min-h-[2rem]`}>
              <TypingAnimation 
                texts={[
                  "Software Development Engineer",
                  "DevOps Specialist",
                  "Machine Learning Engineer",
                  "AI Enthusiast",
                  "Problem Solver"
                ]}
                speed={100}
                deleteSpeed={50}
                pauseTime={2000}
              />
            </h2>

            {/* Status Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 mb-8">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
              {personalInfo.status}
            </div>

            {/* Bio */}
            <p className={`text-lg md:text-xl ${colors.textSecondary} max-w-3xl mx-auto mb-8 leading-relaxed`}>
              {personalInfo.bio}
            </p>

            {/* Social Links */}
            <div className="flex justify-center space-x-6 mb-8">
              {socialLinks.map((social, index) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-3 rounded-full ${colors.cardBackground} shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 ${social.color}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <social.icon className="w-6 h-6" />
                </a>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => scrollToSection('contact')}
                className="px-8 py-3 bg-gradient-to-r accent-gradient-bg accent-gradient-bg-hover text-gray-900 font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Get In Touch
              </button>
              <button
                onClick={() => scrollToSection('projects')}
                className={`px-8 py-3 border-2 ${colors.border} ${colors.text} dark:${colors.text} font-semibold rounded-full ${colors.bgHover} hover:text-gray-900 dark:hover:text-gray-900 transition-all duration-300 transform hover:scale-105`}
              >
                View My Work
              </button>
              <button
                onClick={() => setIsResumeModalOpen(true)}
                className={`px-8 py-3 ${colors.cardBackground} ${colors.textPrimary} font-semibold rounded-full hover:opacity-80 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2`}
              >
                <FaEye className="w-4 h-4" />
                <span>View Resume</span>
              </button>
            </div>

            {/* Scroll Indicator */}
            <div className="mt-8 animate-bounce">
              <button
                onClick={() => scrollToSection('about')}
                className={`p-2 rounded-full ${colors.cardBackground} shadow-lg hover:shadow-xl transition-all duration-300`}
              >
                <FaChevronDown className={`w-5 h-5 ${colors.textSecondary}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Resume Modal */}
      <ResumeModal 
        isOpen={isResumeModalOpen} 
        onClose={() => setIsResumeModalOpen(false)} 
      />
    </section>
  );
};

export default Hero;
